/**
 * The credential layer: how a request says who it is, and which organisation it
 * means to act within.
 *
 * Two headers, and nothing else. Both are built here so that every authenticated
 * call in this client — the three that exist today and whatever Stage 7 adds —
 * constructs them identically.
 */

/**
 * A session token from `POST /auth/login`.
 *
 * Deliberately a wrapper around a string rather than a bare string: it stops a
 * token being passed where an organisation id or an email was meant, and it gives
 * the one place to say the following out loud.
 *
 * The token is **opaque**. It is 43 base64url characters, it is not a JWT, and it
 * carries no claims. There is nothing in it to read: who the caller is, what they
 * may do and when the session dies all come from `GET /auth/session`, never from
 * the token. Any code that splits, decodes or inspects this string is wrong.
 *
 * It is also returned **exactly once**. The server keeps only a SHA-256 digest and
 * cannot recover the original, so there is no flow — no endpoint, no support
 * process — that re-reads a token that has been lost. A lost token means signing in
 * again. Do not design around retrieving one.
 */
export interface SessionCredential {
  readonly token: string;
}

/** Wrap a token from `POST /auth/login` as a credential. Performs no parsing. */
export function sessionCredential(token: string): SessionCredential {
  return { token };
}

/** The header the credential travels in. */
export const AUTHORIZATION_HEADER = "Authorization";

/** The credential's scheme. One scheme, two disjoint credential shapes (see below). */
export const AUTHORIZATION_SCHEME = "Bearer";

/**
 * The header naming the organisation a request acts within.
 *
 * It is a **preference, not an authority**. The server matches the value against
 * the caller's own memberships and derives scope itself; naming an organisation
 * grants nothing. Two consequences the UI must respect:
 *
 *  - A caller who belongs to exactly one organisation may omit it.
 *  - Naming an organisation the caller is not a member of returns **404**, not 403,
 *    and that 404 is byte-identical to a genuine not-found. It is not a signal that
 *    the organisation exists.
 *
 * Any picker built on top of this is driven by `GET /auth/session`'s `memberships`,
 * which is the server's answer rather than the client's assumption.
 */
export const ORGANISATION_HEADER = "X-Halyard-Organisation";

/**
 * What an authenticated call needs beyond its own arguments.
 *
 * `clock` is optional and, when given, is slid by the client on every request it
 * issues — because the server slides the idle deadline on every request it serves.
 * See `./session-lifetime.ts`.
 */
export interface AuthenticatedRequest {
  readonly credential: SessionCredential;
  /** The organisation to act within. Omit when the caller belongs to exactly one. */
  readonly organisationId?: string;
  readonly signal?: AbortSignal;
  readonly clock?: { noteAuthenticatedRequest(at?: Date): void };
}

/** `Authorization: Bearer <credential>`. The only place this string is assembled. */
export function authorizationHeader(credential: SessionCredential): string {
  return `${AUTHORIZATION_SCHEME} ${credential.token}`;
}

/**
 * The headers for an authenticated request.
 *
 * The organisation header is omitted entirely when no organisation was chosen —
 * sending an empty one would be a claim about scope rather than the absence of one.
 */
export function authenticatedHeaders(request: AuthenticatedRequest): Record<string, string> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    [AUTHORIZATION_HEADER]: authorizationHeader(request.credential),
  };

  if (request.organisationId !== undefined && request.organisationId !== "") {
    headers[ORGANISATION_HEADER] = request.organisationId;
  }

  return headers;
}

/**
 * The two credential shapes the single `Authorization: Bearer` header carries.
 *
 * Recorded here as documentation, not as a classifier: the **server** classifies by
 * shape and never by a hint from the client, and this client never needs to tell
 * them apart. It matters for one reason only — `POST /auth/logout` and
 * `GET /auth/session` answer 403 to an API key, because a key has no session behind
 * it. That 403 is about the kind of credential presented, and re-presenting the same
 * key would not help.
 */
export const CREDENTIAL_SHAPES = {
  /** From `POST /auth/login`. 43 base64url characters, opaque. */
  sessionToken: "43 base64url characters",
  /** From `POST /api-keys`. `hal_<8 hex>.<43 base64url>`. For scripting, not for this UI. */
  apiKey: "hal_<8 hex>.<43 base64url>",
} as const;
